import { Archive, Stethoscope, Zap } from "lucide-react";

export const TYPES = [
  { v: "scale", label: "1–10 scale" },
  { v: "yesno", label: "Yes / No" },
  { v: "number", label: "Number" },
];
export const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.label]));
const SCALE_VALUES = Array.from({ length: 10 }, (_, i) => i + 1);
const inputCls = "w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 outline-none";

export function PresetChips({ presets, onPick }) {
  if (presets.length === 0) return null;
  return (
    <div data-testid="tracker-presets">
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1.5"><Zap className="w-3 h-3" /> One-tap presets</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {presets.map((p) => (
          <button key={p.name} type="button" data-testid={`tracker-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onPick(p)}
            className="px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-stone-200 text-stone-700 hover:border-amber-500 hover:text-amber-800 transition">
            + {p.name} <span className="text-stone-400">· {TYPE_LABEL[p.type]}{p.unit ? ` (${p.unit})` : ""}</span>
          </button>
        ))}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Or your own</div>
    </div>
  );
}

export function TrackerForm({ form, onChange, presets, onPick, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-2 mb-4 p-3 rounded-2xl bg-stone-50" data-testid="tracker-form">
      <PresetChips presets={presets} onPick={onPick} />
      <input data-testid="tracker-name" value={form.name} onChange={(e) => onChange({ name: e.target.value })}
        placeholder='e.g. "Hours of sleep", "Took medication", "Self-harm urges"' className={inputCls} />
      <div className="flex flex-wrap gap-1.5">
        {TYPES.map((t) => (
          <button key={t.v} type="button" data-testid={`tracker-type-${t.v}`} onClick={() => onChange({ type: t.v })}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${form.type === t.v ? "bg-amber-600 text-white" : "bg-white border border-stone-200 text-stone-700"}`}>{t.label}</button>
        ))}
      </div>
      {form.type === "number" && (
        <input data-testid="tracker-unit" value={form.unit} onChange={(e) => onChange({ unit: e.target.value })} placeholder="Unit (hrs, cups, mg…)" className={inputCls} />
      )}
      <button type="submit" data-testid="tracker-save" className="amber-btn text-sm w-full">Save tracker</button>
    </form>
  );
}

export function TrackerList({ trackers, onArchive }) {
  if (trackers.length === 0) {
    return <p className="text-sm text-stone-500" data-testid="tracker-empty">Nothing custom yet. Track anything that matters — sleep hours, medication, urges, caffeine.</p>;
  }
  return (
    <ul className="space-y-2">
      {trackers.map((t) => (
        <li key={t.id} data-testid={`tracker-item-${t.id}`} className="flex items-center justify-between gap-2 text-sm border-b border-stone-100 pb-2 last:border-0">
          <div>
            <div className="font-semibold text-stone-800 flex items-center gap-1.5">
              {t.name}
              {t.created_by_role === "clinician" && (
                <span title={`Assigned by ${t.created_by_name}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-800"><Stethoscope className="w-3 h-3" /> coach</span>
              )}
            </div>
            <div className="text-xs text-stone-500">{TYPE_LABEL[t.type]}{t.unit ? ` · ${t.unit}` : ""}</div>
          </div>
          <button type="button" data-testid={`tracker-archive-${t.id}`} onClick={() => onArchive(t.id)} className="text-xs text-stone-400 hover:text-stone-700 inline-flex items-center gap-1">
            <Archive className="w-3 h-3" /> archive
          </button>
        </li>
      ))}
    </ul>
  );
}

function YesNoInput({ t, value, onSet }) {
  return (
    <div className="flex gap-1">
      {[true, false].map((v) => (
        <button key={String(v)} type="button" data-testid={`tracker-${t.id}-${v ? "yes" : "no"}`} onClick={() => onSet(t.id, v)}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition ${value === v ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{v ? "Yes" : "No"}</button>
      ))}
    </div>
  );
}

function ScaleInput({ t, value, onSet }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {SCALE_VALUES.map((n) => (
        <button key={n} type="button" data-testid={`tracker-${t.id}-scale-${n}`} onClick={() => onSet(t.id, n)}
          className={`w-6 h-6 rounded-md text-[11px] font-mono transition ${value === n ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{n}</button>
      ))}
    </div>
  );
}

export function TrackerInputRow({ t, value, onSet, onChange }) {
  return (
    <div className="text-sm" data-testid={`tracker-input-${t.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-stone-700">{t.name}{t.unit ? <span className="text-stone-400"> ({t.unit})</span> : null}</span>
        {t.type === "yesno" && <YesNoInput t={t} value={value} onSet={onSet} />}
        {t.type === "number" && (
          <input type="number" step="any" data-testid={`tracker-${t.id}-number`} value={value ?? ""} placeholder="—"
            onChange={(e) => onChange(t.id, e.target.value === "" ? undefined : +e.target.value)}
            className="w-24 px-3 py-1 rounded-xl border border-stone-200 text-sm text-right focus:border-amber-600 outline-none" />
        )}
      </div>
      {t.type === "scale" && <ScaleInput t={t} value={value} onSet={onSet} />}
    </div>
  );
}
